import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import usePageModal from 'features/Modal/hooks/usePageModal'
import { Post, Thread } from 'lib/_types'
import { toggleThreadBoxModal } from 'store/actions'
import { useNavigate } from 'features/i18n/router'
import { ROUTE_PATH } from 'lib/paths'
import { logEvent } from 'lib/analytics'
import { useCurrentUser } from 'store/api/yay/user'
import { toBooleanString } from 'lib/helpers'

import ConfirmBoxBasic from 'components/ConfirmBoxBasic'
import { MODAL_MODE } from 'components/Modal/config'

const ConfirmBoxMovePostToThread = (props: {
  isActive: boolean
  onClose: Function
  onOk: Function
  data: {
    post: Post
    isAddToExistingThreadEnabled: boolean
  }
}) => {
  const { isActive, onClose, data } = props

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: currentUser } = useCurrentUser()
  const { generatePageModalUrl } = usePageModal()

  useEffect(() => {
    if (isActive) {
      logEvent.thread_convert_start({
        post_id: data?.post?.id,
        group_id: data?.post?.group_id,
        user_role:
          currentUser?.id === data?.post?.user?.id
            ? 'owner'
            : data?.post?.user?.group_user?.is_moderator
            ? 'moderator'
            : 'member',
      })
    }
  }, [isActive])

  const onSelect = (isConfirm: boolean) => {
    logEvent.thread_convert_confirm({
      post_id: data?.post?.id,
      group_id: data?.post?.group_id,
      is_confirm: toBooleanString(isConfirm),
    })
    onClose()
  }

  return (
    <ConfirmBoxBasic
      isActive={isActive}
      title={t('confirm_box_move_post_to_thread.title')}
      content={
        data?.post?._pinned
          ? t('confirm_box_move_post_to_thread.content_pinned')
          : t('confirm_box_move_post_to_thread.content')
      }
      cancelText={t('common.cancel')}
      onClose={() => onSelect(false)}
      onCancel={() => onSelect(false)}
      onOk={() => {
        dispatch(
          toggleThreadBoxModal({
            mode: 'move',
            postIdToMove: data?.post?.id,
            onCreated: ({ thread }: { thread: Thread }) => {
              logEvent.thread_convert_successful({
                thread_id: thread?.id,
                group_id: data?.post?.group_id,
              })

              // Moved post in the created thread may not appear immediately,
              // so need to wait a moment.
              window.setTimeout(() => {
                navigate(
                  ROUTE_PATH.GROUP_THREAD(data?.post?.group_id, thread.id)
                )
              }, 1000)
            },
          })
        )

        onSelect(true)
      }}
      confirmButtonText={t(
        'confirm_box_move_post_to_thread.buttons.create_new'
      )}
      fluidButtons
      {...(data?.isAddToExistingThreadEnabled && {
        extraButtons: [
          {
            content: t(
              'confirm_box_move_post_to_thread.buttons.add_to_existing'
            ),
            onClick: () => {
              navigate(
                generatePageModalUrl(MODAL_MODE.EXISTING_THREADS, {
                  postId: data?.post?.id,
                })
              )
            },
            style: {
              order: 1,
            },
          },
        ],
      })}
    />
  )
}

export default ConfirmBoxMovePostToThread
